import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy, DestroyRef, inject, ChangeDetectorRef, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { ButtonModule } from 'primeng/button';

import { TooltipModule } from 'primeng/tooltip';

import { ModelsService } from '@generated/services/models.service';
import { modelVersionSnapshot } from '@shared/utils/model-version-snapshot';
import { ModelSelector } from '../model-selector/model-selector';
import { TesterSelection } from '../model-tester/model-tester.component';
import { GetModelsHierarchyResult } from '@generated/models';
import { AgentModelVersionDto } from '@generated/models/agent-model-version-dto';

/** Тот же набор версий, в любом порядке. */
function sameIds(a: readonly string[], b: readonly string[] | undefined): boolean {
  if (!b || a.length !== b.length) return false;
  const known = new Set(b);
  return a.every(id => known.has(id));
}

@Component({
  selector: 'app-model-selector-button',
  imports: [ButtonModule, TooltipModule, ModelSelector],
  templateUrl: './model-selector-button.html',
  styleUrl: './model-selector-button.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ModelSelectorButton implements OnInit, OnChanges {
  /** Список model_type_code для фильтра. Строка, а не union, чтобы можно
   *  было прокидывать динамические значения (напр. benchmark-edit
   *  подставляет modelTypeCode() сигнала). */
  @Input() modelTypes: string[] = [];
  @Input() modelsHierarchy: GetModelsHierarchyResult | null = null;
  /** true = parent сейчас перекачивает hierarchy (например по клику
   *  «Recommended only» toggle). Прокидываем в selector — там показывает
   *  spinner-overlay поверх списка моделей. */
  @Input() hierarchyLoading = false;
  @Input() selectedVersionId?: string;
  @Input() selectedVersionIds?: string[];
  @Input() defaultFirstMessage?: string | null;
  @Input() languageId?: string | null;
  @Input() localeId?: string | null;
  @Input() multiSelect = false;
  @Input() allowModelSelect = false;
  @Input() showVersions = false;
  @Input() allowSelectFromTester = false;
  /** Caller's saved additional-property values — forwarded so reopening
   *  the tester for the already-picked model restores the user's tweaks. */
  @Input() currentAdditionalProperties?: Record<string, any> | null;
  /** Caller's saved TTS voice id — pre-selects on tester reopen. */
  @Input() currentVoiceId?: string | null;
  /** Caller's saved LLM sampling temperature — pre-fills the tester's
   *  slider on reopen. Null means "use backend default" and the tester
   *  disables the slider via its enable/disable toggle. */
  @Input() currentTemperature?: number | null;
  /** Same idea for top-K / top-P / max-output-tokens. undefined → не
   *  меняем; null → default-toggle включён; число → преселект слайдера. */
  @Input() currentTopK?: number | null;
  @Input() currentTopP?: number | null;
  @Input() currentMaxOutputTokens?: number | null;
  /**
   * Для multi-select cases (benchmark-edit): map versionId → sampling
   * overrides. Тестер на конкретной версии подсосёт из этой мапы; для
   * версий которых в мапе нет — используются currentTemperature/… или
   * дефолты. Иначе в multi-select контексте один "current*" на весь
   * набор бессмыслен — каждая линковка со своими параметрами.
   */
  @Input() versionSamplingParams?: Record<string, {
    temperature?: number | null;
    topK?: number | null;
    topP?: number | null;
    maxOutputTokens?: number | null;
    additionalProperties?: Record<string, any> | null;
  }> | null;
  /** Gateway codes that activate the tester's custom-LLM editors when
   *  the picked row's gateway matches. */
  @Input() customLlmGatewayCodes: string[] = [];
  /** Initial schemas for the custom-LLM editors — forwarded to the
   *  tester when the row's gateway code matches. */
  @Input() currentCustomHeadersSchema?: import('@generated/models/open-api-properties-schema').OpenApiPropertiesSchema | null;
  @Input() currentCustomBodySchema?: import('@generated/models/open-api-properties-schema').OpenApiPropertiesSchema | null;
  /** Server-side "show all" toggle — default false (only recommended). */
  @Input() includeAll = false;
  @Output() includeAllChange = new EventEmitter<boolean>();
  /** Snapshot'ы моделей/версий, которые нужно инъектировать в селектор
   *  поверх того что вернул бэк — на случай когда у агента сохранена
   *  модель, которой уже нет в текущей иерархии. Прокидывается 1-в-1
   *  в model-selector. */
  @Input() includeVersionSnapshots?: AgentModelVersionDto[] | null;
  /** Version ids to hide from the selector entirely. The button uses
   *  this to also skip excluded versions when collapsing modelId to
   *  firstVersionId for allowModelSelect mode. */
  @Input() excludeVersionIds: string[] = [];
  /**
   * Backend-shipped snapshot of the agent's currently-saved version. The
   * trigger button reads name + active flag straight from this so the
   * label stays correct even when the version has been retired and is no
   * longer returned by the hierarchy endpoint. When the version is
   * inactive we surface a warning icon + tooltip prompting a swap.
   */
  @Input() savedModel?: AgentModelVersionDto | null;
  @Input() label = $localize`:@@modelSelector.selectModel:Select Model`;
  /** Скрыть селектор моделей — при клике на trigger сразу открывается
   *  tester с версией из selectedVersionId. Полезно для сценариев где
   *  модель уже выбрана (напр. Realtime per-locale, где модель — одна
   *  для всех локалей, а через тестер меняется только голос). */
  @Input() disableModelSelection = false;
  /** Пробрасывается в model-selector → model-tester. Показывает admin-only
   *  кнопку «Edit properties» рядом с версией. Дефолт false. */
  @Input() showPropertiesEdit = false;

  @Output() modelSelected = new EventEmitter<string>();
  @Output() modelsSelected = new EventEmitter<string[]>();
  @Output() testerSelected = new EventEmitter<TesterSelection>();

  private cdr = inject(ChangeDetectorRef);
  private modelsService = inject(ModelsService);
  private destroyRef = inject(DestroyRef);

  /**
   * Версия, которой нет ни в снимке от родителя, ни в загруженной
   * иерархии, — её описание добираем сами.
   *
   * Иерархию страница обычно грузит с «только рекомендованные», и версию
   * из полного каталога в ней не найти: на кнопке оставалась общая
   * надпись при верно записанном значении. Чинить это на каждой
   * странице-хозяине значило бы повторять одно и то же столько раз,
   * сколько на платформе мест, где выбирают модель.
   */
  private resolved: AgentModelVersionDto | null = null;
  /** Версия, за которой уже сходили (или пытались) — второй раз не ходим. */
  private resolvedFor: string | null = null;

  // Если что-то выбрано — показываем кнопку; иначе сразу показываем таблицу
  showSelector = true;

  private _currentSelectedVersionId?: string;
  private _currentSelectedVersionIds?: string[];

  ngOnInit(): void {
    this.showSelector = !this.hasSelection;
    this.resolveUnknownVersion();
    this.resolveVoiceName();
  }

  /**
   * Parents own the selection state — when the input changes (e.g. the
   * fallback list got reordered by drag-drop), clear our local "remember
   * what was just picked" cache so the new input wins. Without this the
   * button at DOM slot N would keep displaying the previously-picked
   * version even after the array swap.
   *
   * И сворачиваем раскрытую таблицу: выбор мог прийти не отсюда. Пока
   * состояние «раскрыто» менялось только кликами, выбор моделей, сделанный
   * встроенным агентом, выглядел как «ничего не произошло» — таблица
   * оставалась во весь экран, кнопки с названием модели не было видно
   * вовсе, а подсветка обводила эту самую таблицу.
   */
  ngOnChanges(changes: SimpleChanges): void {
    let pickedElsewhere = false;

    if (changes['selectedVersionId'] && !changes['selectedVersionId'].firstChange) {
      const { currentValue: next, previousValue: prev } = changes['selectedVersionId'];
      pickedElsewhere ||= !!next && next !== prev && next !== this._currentSelectedVersionId;
      this._currentSelectedVersionId = undefined;
    }
    if (changes['selectedVersionIds'] && !changes['selectedVersionIds'].firstChange) {
      // Сравниваем по содержимому: родитель вправе отдавать новый массив
      // на каждый проход — сворачивать каталог ему в ответ значило бы не
      // давать выбрать вторую модель.
      const next = changes['selectedVersionIds'].currentValue as string[] | undefined;
      const prev = changes['selectedVersionIds'].previousValue as string[] | undefined;
      pickedElsewhere ||=
        !!next?.length && !sameIds(next, prev) && !sameIds(next, this._currentSelectedVersionIds);
      this._currentSelectedVersionIds = undefined;
    }

    if (pickedElsewhere && this.showSelector) {
      this.showSelector = false;
      this.cdr.markForCheck();
    }

    this.resolveUnknownVersion();
    this.resolveVoiceName();
  }

  /**
   * Дотягивает описание выбранной версии, если подписать её нечем.
   *
   * Ждём иерархию: пока её нет, «не нашли» ничего не значит. Один запрос
   * на версию, ошибку глотаем — подпись останется общей, а значение от
   * этого не портится.
   */
  private resolveUnknownVersion(): void {
    const id = this._currentSelectedVersionId || this.selectedVersionId;
    if (!id || this.multiSelect) return;
    if (this.resolvedFor === id) return;
    if (!this.modelsHierarchy) return; // иерархия ещё едет — рано судить
    if (this.savedModel?.id === id) return;
    if (this.snapshotFor(id) || this.findModelNameByVersionId(id)) return;

    this.resolvedFor = id;
    this.modelsService
      .apiModelsVersionsModelVersionIdGet({ modelVersionId: id })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: full => {
          this.resolved = modelVersionSnapshot(full);
          this.cdr.markForCheck();
        },
        error: () => {
          this.resolved = null;
          this.cdr.markForCheck();
        },
      });
  }

  /** Снимок версии: присланный родителем или добранный нами. */
  private snapshotFor(versionId: string): AgentModelVersionDto | null {
    if (this.resolved?.id === versionId) return this.resolved;
    return (this.includeVersionSnapshots ?? []).find(v => v.id === versionId) ?? null;
  }

  /** Снимки для таблицы: к присланным добавляем добранную версию. */
  get effectiveSnapshots(): AgentModelVersionDto[] {
    const fromParent = this.includeVersionSnapshots ?? [];
    if (!this.resolved || fromParent.some(v => v.id === this.resolved!.id)) return fromParent;
    return [...fromParent, this.resolved];
  }

  /**
   * Свернуть таблицу к кнопке — чтобы то, что сейчас будет меняться, было
   * видно целиком. Зовётся страницей перед тем, как показать предложение
   * встроенного агента: карточка «Применить / Отклонить» встаёт под полем,
   * а под раскрытой на пол-экрана таблицей её никто не найдёт.
   */
  collapse(): void {
    if (!this.showSelector) return;
    this.showSelector = false;
    this.cdr.markForCheck();
  }

  get hasSelection(): boolean {
    if (this.multiSelect) {
      const ids = this._currentSelectedVersionIds || this.selectedVersionIds;
      return !!(ids && ids.length > 0);
    }
    return !!(this._currentSelectedVersionId || this.selectedVersionId);
  }

  get buttonLabel(): string {
    if (this.multiSelect) {
      const currentIds = this._currentSelectedVersionIds || this.selectedVersionIds;
      const count = currentIds?.length || 0;
      if (count === 0) return this.label;
      return $localize`:@@modelSelector.modelsSelected:${count}:count: model(s) selected`;
    }

    const currentId = this._currentSelectedVersionId || this.selectedVersionId;
    // Prefer the backend snapshot — it survives the version being dropped
    // from the hierarchy (e.g. retired by a sync). Falls back to a live
    // hierarchy lookup, then to the default label.
    let model: string | null = null;
    if (this.savedModel && currentId && this.savedModel.id === currentId) {
      model = this.formatSavedModelLabel(this.savedModel);
    } else if (currentId) {
      const snapshot = this.snapshotFor(currentId);
      model = snapshot
        ? this.formatSavedModelLabel(snapshot)
        : this.findModelNameByVersionId(currentId);
    }
    if (!model) return this.label;

    // Голос — часть выбора, а не деталь внутри диалога: у TTS и у
    // realtime-модели он решает, как звучит агент. Показываем прямо на
    // кнопке, иначе чтобы узнать текущий голос приходится открывать
    // тестер.
    const voice = this.voiceName;
    return voice
      ? $localize`:@@modelSelector.modelWithVoice:${model}:model: · ${voice}:voice:`
      : model;
  }

  /**
   * Имя выбранного голоса. Наружу приходит только его id, а справочник
   * голосов у каждой версии модели свой, поэтому имя добираем сами —
   * ровно по той же причине, по какой компонент сам резолвит неизвестную
   * версию: иначе это пришлось бы повторять на каждой странице, где
   * выбирают модель.
   */
  private get voiceName(): string | null {
    const voiceId = this.currentVoiceId;
    if (!voiceId || this.resolvedVoiceFor !== voiceId) return null;
    return this.resolvedVoiceName;
  }

  private resolvedVoiceName: string | null = null;
  /** Голос, за которым уже ходили (или пытались) — второй раз не ходим. */
  private resolvedVoiceFor: string | null = null;
  private voiceRequestFor: string | null = null;

  /**
   * Зовётся из ngOnInit/ngOnChanges, а не из геттера подписи: запрос в
   * геттере уходил бы прямо посреди проверки изменений — побочный эффект
   * на пути чтения. Рядом `resolveUnknownVersion` сделан так же.
   */
  private resolveVoiceName(): void {
    const voiceId = this.currentVoiceId;
    const versionId = this._currentSelectedVersionId || this.selectedVersionId;
    if (!voiceId || !versionId) {
      this.resolvedVoiceName = null;
      this.resolvedVoiceFor = null;
      return;
    }
    if (this.voiceRequestFor === voiceId) return;
    this.voiceRequestFor = voiceId;
    this.modelsService
      .apiModelsVersionsModelVersionIdVoicesGet({ modelVersionId: versionId })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: result => {
          const found = (result.voices ?? []).find(v => v.id === voiceId);
          this.resolvedVoiceName = found?.name ?? found?.code ?? null;
          this.resolvedVoiceFor = voiceId;
          this.cdr.markForCheck();
        },
        error: () => {
          // Имя не достали — покажем одну модель. Пустая кнопка была бы
          // хуже, чем кнопка без голоса.
          this.resolvedVoiceName = null;
          this.resolvedVoiceFor = voiceId;
          this.cdr.markForCheck();
        },
      });
  }

  /** True when we should surface the "this model is inactive — pick a
   *  replacement" affordance next to the label. */
  get savedModelIsInactive(): boolean {
    const currentId = this._currentSelectedVersionId || this.selectedVersionId;
    if (!currentId) return false;
    const known = this.savedModel?.id === currentId ? this.savedModel : this.snapshotFor(currentId);
    return known?.is_active === false;
  }

  readonly inactiveTooltip = $localize`:@@modelSelector.inactiveTooltip:Эта модель сейчас недоступна — пожалуйста, выберите другую.`;

  private formatSavedModelLabel(snapshot: AgentModelVersionDto): string {
    const author = snapshot.author?.name;
    const modelName = snapshot.model?.name;
    const versionName = snapshot.name;
    const head = author && modelName ? `${author} ${modelName}` : (modelName || '');
    const parts = [head, versionName].filter(Boolean);
    return parts.length ? parts.join(' ') : (modelName || this.label);
  }

  // Текущие id для подсветки в селекторе при повторном открытии
  get effectiveSelectedIds(): string[] {
    if (this.multiSelect) {
      return this._currentSelectedVersionIds || this.selectedVersionIds || [];
    }
    const id = this._currentSelectedVersionId || this.selectedVersionId;
    return id ? [id] : [];
  }

  toggleSelector(): void {
    this.showSelector = !this.showSelector;
    this.cdr.markForCheck();
  }

  onCancelled(): void {
    this.showSelector = false;
    this.cdr.markForCheck();
  }

  onSelectionConfirmed(result: string | string[]): void {
    if (this.multiSelect) {
      if (result && Array.isArray(result)) {
        // В режиме модели — конвертим каждый modelId в firstVersionId
        const ids = this.allowModelSelect && !this.showVersions
          ? result.map(id => this.firstVersionIdOfModel(id) ?? id)
          : result;
        this._currentSelectedVersionIds = ids;
        this.modelsSelected.emit(ids);
      }
    } else {
      if (result && typeof result === 'string') {
        // В режиме модели — конвертим modelId в firstVersionId, наружу всегда уходит versionId
        const versionId = this.allowModelSelect && !this.showVersions
          ? (this.firstVersionIdOfModel(result) ?? result)
          : result;
        this._currentSelectedVersionId = versionId;
        this.modelSelected.emit(versionId);
      }
    }
    this.showSelector = false;
    this.cdr.markForCheck();
  }

  private firstVersionIdOfModel(modelId: string): string | null {
    if (!this.modelsHierarchy?.model_types) return null;
    const excluded = new Set(this.excludeVersionIds);
    for (const type of this.modelsHierarchy.model_types) {
      for (const gateway of type.gateways || []) {
        const model = (gateway.models || []).find(m => m.id === modelId);
        if (model) {
          const candidate = (model.versions || []).find(v => v.id && !excluded.has(v.id));
          return candidate?.id || null;
        }
      }
    }
    return null;
  }

  private findModelNameByVersionId(versionId: string): string | null {
    if (!this.modelsHierarchy?.model_types) return null;

    for (const modelType of this.modelsHierarchy.model_types) {
      if (!modelType.gateways) continue;
      for (const gateway of modelType.gateways) {
        if (!gateway.models) continue;
        for (const model of gateway.models) {
          if (!model.versions) continue;
          const version = model.versions.find(v => v.id === versionId);
          if (version) {
            const author = model.author?.name;
            const head = author ? `${author} ${model.name}` : model.name;
            return `${head} ${version.name}`;
          }
        }
      }
    }
    return null;
  }
}
